import httpClient from '../../../api/httpClient';
import API_CONFIG from '../../../config/api.config';
import { 
  AppointmentOrderResponseDto, 
  CreateAppointmentOrderDto, 
  UpdateAppointmentOrderDto,
  AppointmentOrderFilterParams
} from './types';

// Standard API response type used across the application
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

// Standard error response type
interface ErrorResponse {
  message: string;
  statusCode: number;
  error?: string;
}

// Enhanced error handler with detailed logging
const handleApiError = (error: any, context: string = ''): never => {
  console.error(`[API Error] ${context}`, {
    message: error.message,
    status: error.response?.status,
    statusText: error.response?.statusText,
    url: error.config?.url,
    method: error.config?.method,
    requestData: error.config?.data,
    responseData: error.response?.data,
    stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
  });

  const errorResponse = error.response?.data as ErrorResponse;
  const errorMessage = errorResponse?.message || 
                     error.message || 
                     `An unexpected error occurred${context ? ` while ${context}` : ''}`;
  
  const apiError = new Error(errorMessage);
  (apiError as any).status = error.response?.status;
  (apiError as any).code = error.response?.data?.code;
  
  throw apiError;
};

const ENDPOINT = API_CONFIG.ENDPOINTS.APPOINTMENT_ORDERS;

/**
 * Maps the API response DTO to the frontend model if needed
 */
const mapOrderDtoToModel = (dto: AppointmentOrderResponseDto): AppointmentOrderResponseDto => {
  return {
    ...dto,
    // Add any transformations here if needed
  };
};

export const appointmentOrderApi = {
  /**
   * Create a new appointment order
   */
  create: async (data: CreateAppointmentOrderDto): Promise<AppointmentOrderResponseDto> => {
    try {
      const response = await httpClient.post<ApiResponse<AppointmentOrderResponseDto>>(ENDPOINT, data);
      return mapOrderDtoToModel(response.data.data);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get all appointment orders with optional filters
   */
  getAll: async (params?: AppointmentOrderFilterParams): Promise<AppointmentOrderResponseDto[]> => {
    try {
      const response = await httpClient.get<ApiResponse<AppointmentOrderResponseDto[]>>(ENDPOINT, { 
        params
      });
      return response.data.data.map(mapOrderDtoToModel);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Get all orders for a specific appointment
   */
  /**
   * Get all orders for today's appointments
   */
  getTodaysOrders: async (): Promise<Array<{
    appointmentId: string;
    patientName: string;
    doctorName: string;
    time: string | Date;
    orderCount: number;
    totalAmount: number;
    orders: AppointmentOrderResponseDto[];
  }>> => {
    try {
      console.log('Fetching today\'s appointments with orders...');
      const response = await httpClient.get<ApiResponse<Array<{
        appointmentId: string;
        patientName: string;
        doctorName: string;
        time: string | Date;
        orderCount: number;
        totalAmount: number;
        orders: AppointmentOrderResponseDto[];
      }>>>(
        `${ENDPOINT}/today`
      );
      
      console.log('Today\'s appointments with orders:', response.data);
      return response.data.data;
    } catch (error) {
      if (typeof error === 'object' && error !== null) {
        const err = error as any;
        console.error('Error fetching today\'s appointments with orders:', err);
        if (err.response) {
          console.error('Error response data:', err.response.data);
          console.error('Error response status:', err.response.status);
          console.error('Error response headers:', err.response.headers);
        } else if (err.request) {
          console.error('No response received:', err.request);
        } else {
          console.error('Error setting up request:', err.message);
        }
        throw handleApiError(err, 'fetching today\'s appointments with orders');
      } else {
        console.error('Unknown error:', error);
        throw handleApiError(error, 'fetching today\'s appointments with orders');
      }
    }
  },

  /**
   * Get all orders for a specific appointment
   */
  getByAppointmentId: async (appointmentId: string): Promise<AppointmentOrderResponseDto[]> => {
    console.log(`[API] Fetching orders for appointment ${appointmentId}`);
    try {
      const response = await httpClient.get<ApiResponse<AppointmentOrderResponseDto[]>>(
        `${ENDPOINT}/appointment/${appointmentId}`
      );
      
      console.log(`[API] Successfully fetched ${response.data.data?.length || 0} orders for appointment ${appointmentId}`);
      return response.data.data || [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.log(`[API] No orders found for appointment ${appointmentId}`);
        return [];
      }
      console.error('[API] Error fetching appointment orders:', {
        error: error.message,
        status: error.response?.status,
        url: error.config?.url,
        method: error.config?.method,
      });
      return handleApiError(error, `fetching orders for appointment ${appointmentId}`);
    }
  },

  /**
   * Get a single appointment order by ID
   */
  getById: async (id: string): Promise<AppointmentOrderResponseDto> => {
    try {
      const response = await httpClient.get<ApiResponse<AppointmentOrderResponseDto>>(
        `${ENDPOINT}/${id}`
      );
      return mapOrderDtoToModel(response.data.data);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update an existing appointment order
   */
  update: async (id: string, data: UpdateAppointmentOrderDto): Promise<AppointmentOrderResponseDto> => {
    try {
      const response = await httpClient.patch<ApiResponse<AppointmentOrderResponseDto>>(
        `${ENDPOINT}/${id}`,
        data
      );
      return mapOrderDtoToModel(response.data.data);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Delete an appointment order
   */
  delete: async (id: string): Promise<void> => {
    try {
      await httpClient.delete<ApiResponse<void>>(`${ENDPOINT}/${id}`);
    } catch (error) {
      handleApiError(error);
    }
  },

  /**
   * Update appointment order status
   */
  updateStatus: async (id: string, status: string): Promise<AppointmentOrderResponseDto> => {
    try {
      const response = await httpClient.patch<ApiResponse<AppointmentOrderResponseDto>>(
        `${ENDPOINT}/${id}/status`,
        { status }
      );
      return mapOrderDtoToModel(response.data.data);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Update payment status for an appointment order
   */
  updatePaymentStatus: async (
    id: string,
    financialStatus: string,
    paidAmount?: number
  ): Promise<AppointmentOrderResponseDto> => {
    try {
      const response = await httpClient.patch<ApiResponse<AppointmentOrderResponseDto>>(
        `${ENDPOINT}/${id}/payment-status`,
        { financialStatus, paidAmount }
      );
      return mapOrderDtoToModel(response.data.data);
    } catch (error) {
      return handleApiError(error);
    }
  },
};
